import StyledAuthContainer from '@/components/auth/StyledAuthContainer';
import StyledButton from '@/components/styled/StyledButton';
import StyledInput from '@/components/styled/StyledInput';
import { PATHS } from '@/constants/Paths';
import { useAuth } from '@/hooks/useAuth';
import { AuthStatus } from '@/types/auth/AuthStatus';
import { LoginFormData, LoginSchema } from '@/types/auth/LoginFormData';
import { UserType } from '@/types/auth/UserType';
import { getDashboardPath } from '@/utils/authProfile';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useTranslation } from '@/i18n';

const LoginPage: React.FC = () => {
  const { login, selectProfile } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<LoginFormData>({
    resolver: zodResolver(LoginSchema),
    mode: 'onChange',
    defaultValues: { email: '', password: '' },
  });

  const navigateByUserType = (userType: UserType) => {
    navigate(getDashboardPath(userType));
  };

  const submitForm: SubmitHandler<LoginFormData> = async (data) => {
    setErrorMessage(undefined);
    setIsSubmitting(true);

    try {
      const result = await login(data);

      if (result.status !== AuthStatus.OK) {
        setErrorMessage(t('auth.invalidCredentials'));
        return;
      }

      if (result.requiresProfileSelection) {
        const defaultUserId = result.defaultUserId;
        const selectionToken = result.selectionToken;

        if (defaultUserId && selectionToken) {
          const selectResult = await selectProfile(
            defaultUserId,
            selectionToken,
          );
          if (selectResult.status === AuthStatus.OK && selectResult.userType) {
            navigateByUserType(selectResult.userType);
            return;
          }
        }

        if (result.profiles && result.profiles.length > 1) {
          navigate(PATHS.auth.selectProfile);
          return;
        }
      }

      if (!result.userType) {
        setErrorMessage(t('auth.invalidCredentials'));
        return;
      }

      navigateByUserType(result.userType);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <StyledAuthContainer
      title={t('auth.login')}
      footerMessage={t('auth.noAccountYet')}
      footerLink={
        <StyledButton
          title={t('auth.signUp')}
          variant="link"
          size="sm"
          className="h-6 px-1 py-0 gap-1 text-brand-600"
          onClick={() => navigate(PATHS.auth.register)}
        />
      }
    >
      <form
        className="flex w-full flex-col gap-6"
        onSubmit={handleSubmit(submitForm)}
      >
        <div className="flex flex-col w-full gap-5">
          <StyledInput
            control={control}
            title={t('auth.email')}
            name="email"
            type="email"
            placeholder={t('auth.enterEmail')}
          />

          <StyledInput
            control={control}
            title={t('auth.password')}
            name="password"
            type="password"
          />
        </div>
        <StyledButton
          className="gap-1 w-36 px-0.5 text-gray-600"
          variant="link"
          size="sm"
          title={t('auth.forgotPassword')}
        />

        {errorMessage && (
          <p className="text-xs text-error-500">{errorMessage}</p>
        )}

        <div className="flex w-full flex-col gap-2">
          <StyledButton
            title={t('auth.signInButton')}
            className="px-4 py-2.5 gap-1.5 rounded-lg"
            disabled={!isValid || isSubmitting}
          />
        </div>
      </form>
    </StyledAuthContainer>
  );
};

export default LoginPage;
